import { useState, useEffect } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { Lightning, CheckCircle, XCircle, ArrowCounterClockwise, EnvelopeSimple } from '@phosphor-icons/react';

const STATUS_STYLES = {
  new: 'bg-blue-50 text-blue-700 border-blue-200',
  approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  rejected: 'bg-slate-100 text-slate-500 border-slate-200',
};

export default function VoiceRulebookPage() {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get('/voice/suggestions');
      setSuggestions(data.suggestions);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load suggestions');
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const setStatus = async (sid, status) => {
    try {
      await api.put(`/voice/suggestions/${sid}`, { status });
      setSuggestions(prev => prev.map(s => s.suggestion_id === sid ? { ...s, status } : s));
      toast.success(status === 'approved' ? 'Added to the rulebook — future corrections will use it' : `Marked ${status}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };

  const pending = suggestions.filter(s => s.status === 'new');
  const approved = suggestions.filter(s => s.status === 'approved');
  const rejected = suggestions.filter(s => s.status === 'rejected');

  const Row = ({ s, actions }) => (
    <div className="flex items-start gap-3 border border-slate-200 rounded-lg p-4 bg-white" data-testid={`suggestion-row-${s.suggestion_id}`}>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-slate-800 leading-relaxed">{s.text}</p>
        <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-slate-400">
          <span className={`px-2 py-0.5 rounded-full border font-bold ${STATUS_STYLES[s.status]}`}>{s.status}</span>
          {s.email && <span className="inline-flex items-center gap-1"><EnvelopeSimple size={12} />{s.email}</span>}
          <span>{new Date(s.created_at).toLocaleDateString()}</span>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">{actions}</div>
    </div>
  );

  return (
    <div className="p-6 max-w-4xl mx-auto" data-testid="voice-rulebook-page">
      <div className="mb-6">
        <h1 className="text-2xl font-black text-slate-900 flex items-center gap-2">
          <Lightning size={22} weight="bold" className="text-[#F39C12]" />Voiceover Rulebook
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Suggestions from the free Voiceover Corrector (/voice). Approved rules are automatically
          added to the cadence AI's instructions — every future correction uses them.
        </p>
      </div>

      {loading ? (
        <p className="text-sm text-slate-400">Loading…</p>
      ) : (
        <div className="space-y-8">
          <section data-testid="rulebook-pending-section">
            <h2 className="text-sm font-black uppercase tracking-wider text-slate-500 mb-3">Pending review ({pending.length})</h2>
            {pending.length === 0 ? (
              <p className="text-sm text-slate-400 border border-dashed border-slate-200 rounded-lg p-4">No new suggestions — share /voice to collect more.</p>
            ) : (
              <div className="space-y-3">
                {pending.map(s => (
                  <Row key={s.suggestion_id} s={s} actions={<>
                    <button onClick={() => setStatus(s.suggestion_id, 'approved')}
                      className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold px-3 py-2 rounded-lg"
                      data-testid={`approve-btn-${s.suggestion_id}`}>
                      <CheckCircle size={14} weight="bold" />Approve
                    </button>
                    <button onClick={() => setStatus(s.suggestion_id, 'rejected')}
                      className="inline-flex items-center gap-1.5 border border-slate-300 hover:bg-slate-50 text-slate-600 text-xs font-bold px-3 py-2 rounded-lg"
                      data-testid={`reject-btn-${s.suggestion_id}`}>
                      <XCircle size={14} weight="bold" />Reject
                    </button>
                  </>} />
                ))}
              </div>
            )}
          </section>

          <section data-testid="rulebook-approved-section">
            <h2 className="text-sm font-black uppercase tracking-wider text-slate-500 mb-3">The rulebook — live in the AI ({approved.length})</h2>
            {approved.length === 0 ? (
              <p className="text-sm text-slate-400 border border-dashed border-slate-200 rounded-lg p-4">Nothing approved yet.</p>
            ) : (
              <div className="space-y-3">
                {approved.map(s => (
                  <Row key={s.suggestion_id} s={s} actions={
                    <button onClick={() => setStatus(s.suggestion_id, 'new')}
                      className="inline-flex items-center gap-1.5 border border-slate-300 hover:bg-slate-50 text-slate-600 text-xs font-bold px-3 py-2 rounded-lg"
                      data-testid={`retire-btn-${s.suggestion_id}`}>
                      <ArrowCounterClockwise size={14} weight="bold" />Retire
                    </button>
                  } />
                ))}
              </div>
            )}
          </section>

          {rejected.length > 0 && (
            <section data-testid="rulebook-rejected-section">
              <h2 className="text-sm font-black uppercase tracking-wider text-slate-400 mb-3">Rejected ({rejected.length})</h2>
              <div className="space-y-3 opacity-60">
                {rejected.map(s => (
                  <Row key={s.suggestion_id} s={s} actions={
                    <button onClick={() => setStatus(s.suggestion_id, 'new')}
                      className="text-xs text-slate-500 hover:text-slate-700 font-bold px-2 py-2"
                      data-testid={`restore-btn-${s.suggestion_id}`}>Restore</button>
                  } />
                ))}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}
